import { useEffect, useRef } from "react";
import type { TourStepInteraction } from "../../types/tours";
import { canAutoPressAnchor } from "./tourAnchors";
import {
  anchorSelector,
  findRenderedAnchorElement,
  isTourInitiatedPress,
  pressAnchorElement,
  scheduleFrame,
  TOUR_OVERLAY_ATTRIBUTE,
} from "./tourDom";

export interface StepInteractionInput {
  /** Already defaulted and already gated on the step having an anchor to work with. */
  interaction: TourStepInteraction;
  /** The step's anchor id, or null for a centered card. */
  anchorId: string | null;
  /** `${tourKey}:${stepKey}`, unique across the chained tours the overlay stays mounted for. */
  stepKey: string;
  /** Advances the tour. Called at most once per `ClickToContinue` step. */
  onAdvance: () => void;
  /**
   * The tour has just pressed this step's control itself. Records the step against the run — the
   * same record a coach's own click leaves — without advancing, and hands focus back to the bubble.
   */
  onOpened: () => void;
}

/**
 * The two interactive step behaviours, both of which work through the real control rather than a
 * simulation of it.
 *
 * `ClickToContinue` watches for the coach activating the anchor and advances the tour. The listener
 * is on `document` in the **capture** phase, so a control whose own handler stops propagation
 * (several popover triggers do) cannot hide the click, and it does not preventDefault or stop
 * anything itself: the control still opens what it opens. A keyboard Enter or Space on a button
 * fires the same click, so no separate key handling is needed, and nothing advances the tour except
 * that one control.
 *
 * `OpenAnchor` clicks the control itself once the step is on screen, so the coach reads the step
 * with the popup already open. It fires **once per step per run**: coming back to the step with
 * Back must not open it a second time (which, for a toggle, would close it). Only the controls in
 * `AUTO_PRESS_ANCHORS` are ever pressed.
 *
 * The "already pressed" record is the run's own `satisfiedStepKeys`, reached through `onOpened`,
 * not a ref in here. One record, four readers: this effect (do not press twice), the overlay (a
 * step with nothing left to press is not interactive, so the page gets its shield back), Back
 * (walk past it) and the end of the run (close again what it opened). A ref could only ever be
 * read by the first of those, which is how Back came to
 * land on a spent `OpenAnchor` step — describing a panel that was no longer on screen, over a page
 * left fully live. It also lived for the life of the overlay rather than the run, so an admin
 * preview replacing the same tour inherited it and silently refused the press on a fresh run.
 *
 * A control that mounts late needs no watch of its own here. `interaction` is `OpenAnchor` only
 * once the overlay has a measured rect for the anchor, so this effect re-runs the moment the
 * control appears — that gate is fed by `useAnchorState`, which already observes the page for
 * exactly this.
 */
export const useStepInteraction = ({
  interaction,
  anchorId,
  stepKey,
  onAdvance,
  onOpened,
}: StepInteractionInput) => {
  const onAdvanceRef = useRef(onAdvance);
  const onOpenedRef = useRef(onOpened);
  useEffect(() => {
    onAdvanceRef.current = onAdvance;
    onOpenedRef.current = onOpened;
  }, [onAdvance, onOpened]);

  useEffect(() => {
    if (interaction !== "ClickToContinue" || !anchorId) return;
    const selector = anchorSelector(anchorId);
    let advanced = false;

    const handleClick = (event: MouseEvent) => {
      if (advanced || !(event.target instanceof Element)) return;
      // A press the tour made itself is not the coach doing as the step asked. The end-of-run
      // tidy-up closes what the run opened, and an admin can author a tour whose last step watches
      // the very anchor the tidy-up presses — which would record the run a second time, with
      // whatever outcome the advance produced.
      if (isTourInitiatedPress()) return;
      const hit = event.target.closest(selector);
      // The overlay carries no anchors of its own, but a tour running over the admin pick-mode
      // layer or a future in-bubble anchor must not count as the coach pressing the control.
      if (!hit || hit.closest(`[${TOUR_OVERLAY_ATTRIBUTE}]`)) return;
      // Once only: a double click would otherwise skip the step after this one.
      advanced = true;
      onAdvanceRef.current();
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [interaction, anchorId, stepKey]);

  useEffect(() => {
    if (interaction !== "OpenAnchor" || !anchorId) return;
    if (!canAutoPressAnchor(anchorId)) return;

    // A frame late, so the step's own render (and anything the previous step's click opened) has
    // landed before the page is touched.
    return scheduleFrame(() => {
      const element = findRenderedAnchorElement(anchorId);
      // Gone again between the gate and this frame. The gate re-runs this effect when it comes
      // back, and nothing is recorded, so the press is not lost.
      if (!element) return;
      // A trigger that says it is already open would be closed by a press, not opened: the coach
      // got there first, or an earlier step asked them to. The overlay reads the same flag and has
      // already made the step read-and-Next, so this is the belt to that braces. A trigger with no
      // `aria-expanded` says nothing either way, so the press goes ahead.
      if (element.getAttribute("aria-expanded") === "true") return;
      pressAnchorElement(element);
      // Recorded only for a press that actually landed: that is what makes this step read-and-Next
      // from here on, and what the end of the run reads to close again what it opened.
      onOpenedRef.current();
    });
  }, [interaction, anchorId, stepKey]);
};
